import * as z from 'zod'

/**
 * This schema verifies the payload received by the React application
 * has a known shape. The content inside "contentfulRawJsonResponse" should
 * be later validated by other schemas.
 */

/**
 * Zod schemas and type guards for validating payloads used by generic templates
 * (e.g., index pages, resource details, confirmations). These schemas define the
 * expected structure of data passed from the Rails backend to React templates.
 */

export const BasePayloadSchema = z.lazy(() =>
  z.object({
    contentfulRawJsonResponse: z.looseObject({}),
    userLoggedIn: z.boolean().optional(),
  }),
)

export const BreadcrumbSchema = z.object({
  name: z.string(),
  href: z.string(),
})

export const IndexPayloadSchema = z.lazy(() =>
  BasePayloadSchema.unwrap().extend({
    additionalProps: z
      .object({
        totalPages: z.number(),
        filters: z.array(
          z.object({
            value: z.string(),
            label: z.string(),
            checkboxes: z.array(
              z.object({
                label: z.string(),
                value: z.string().optional(),
                isChecked: z.boolean().optional(),
              }),
            ),
          }),
        ),
        sortOptions: z
          .array(
            z.object({
              label: z.string(),
              value: z.string(),
            }),
          )
          .optional(),
        currentSort: z.string().optional(),
        showSpotlightCard: z.boolean().optional(),
      })
      .optional(),
  }),
)

export const ResourceDetailsPayloadSchema = z.lazy(() =>
  BasePayloadSchema.unwrap().extend({
    breadcrumbLinks: z.array(BreadcrumbSchema).optional(),
    indexPath: z.string().optional(),
  }),
)

export const ResourceConfirmationPayloadSchema = z.lazy(() =>
  BasePayloadSchema.unwrap().extend({
    breadcrumbLinks: z.array(BreadcrumbSchema).optional(),
  }),
)

export const TopicPayloadSchema = z.object({
  title: z.string().optional(),
  topic: z.string(),
  topics: z.array(
    z.object({
      id: z.string(),
      preferred_label: z.string(),
      definition: z.string().optional(),
    }),
  ),
})

export type TopicPayload = z.infer<typeof TopicPayloadSchema>

export type IndexPayload = z.infer<typeof IndexPayloadSchema>
export type ResourceDetailsPayload = z.infer<typeof ResourceDetailsPayloadSchema>
export type ResourceConfirmationPayload = z.infer<typeof ResourceConfirmationPayloadSchema>

export function toIndexPayload(payload: unknown): IndexPayload {
  return IndexPayloadSchema.parse(payload)
}

export function toResourceDetailsPayload(payload: unknown): ResourceDetailsPayload {
  return ResourceDetailsPayloadSchema.parse(payload)
}

export function toResourceConfirmationPayload(payload: unknown): ResourceConfirmationPayload {
  return ResourceConfirmationPayloadSchema.parse(payload)
}

export function toTopicPayload(payload: unknown): TopicPayload {
  return TopicPayloadSchema.parse(payload)
}
